import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, inject, Inject, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateDirective, TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { AppService } from '@app/app.service';
import { APP_VERSION_TOKEN } from '@app/app.version';
import { BackButtonComponent } from '@common/back-button/back-button.component';
import { FooterContentComponent } from '@common/footer-content/footer-content.component';
import { HeaderContentComponent } from '@common/header-content/header-content.component';
import { MenuButtonComponent } from '@common/menu-button/menu-button.component';
import { MenuComponent } from '@common/menu/menu.component';
import { RouteAnnouncerService } from '@common/route-announcer.service';
import { ToolbarComponent } from '@common/toolbar/toolbar.component';
import { IdentityServicesService } from '@identity/identity-services.service';
import { IDENTITY_API_URL_TOKEN } from '@identity/identity-url-token';
import { SasSpinnerComponent } from '@shared/sas-spinner/sas-spinner.component';

const STYLESHEET = 'STYLESHEET';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        RouterOutlet,
        CommonModule,
        HeaderContentComponent,
        FooterContentComponent,
        MenuComponent,
        MenuButtonComponent,
        ToolbarComponent,
        SasSpinnerComponent,
        BackButtonComponent
    ],
    providers: [IdentityServicesService, TranslateService, TranslatePipe, TranslateDirective, TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})

export class AppComponent {
    loadedTemplate!: TemplateRef<any>;
    @ViewChild('defaultTemplate', { static: true }) defaultTemplate!: TemplateRef<any>;
    @ViewChild('embeddedTemplate', { static: true }) embeddedTemplate!: TemplateRef<any>;
    @ViewChild('content') private content!: ElementRef;

    title = 'agile-hub';
    appService = inject(AppService);
    identity = inject(IdentityServicesService);
    activatedRoute = inject(ActivatedRoute);
    renderer = inject(Renderer2);
    routeAnnouncerService = inject(RouteAnnouncerService);
    private destroyRef = inject(DestroyRef);
    prevRoute: any = {};

    constructor(@Inject(IDENTITY_API_URL_TOKEN) private identityUrl: string,
        @Inject(APP_VERSION_TOKEN) private version: string,
        private router: Router,
        private translate: TranslateService
    ) { }

    ngOnInit() {
        this.routeAnnouncerService.init();
        this.removeIndexSpinner();

        if (window.location.href.indexOf("/embedded/") > -1) {
            this.loadedTemplate = this.embeddedTemplate;
        }
        else {
            this.loadedTemplate = this.defaultTemplate;
        }
        const nonRedirectUrls = ['error', 'signIn', 'signup', 'reset-password', 'forgot-password', 'authorize'];

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event: NavigationStart | NavigationEnd) => {
                if (event instanceof NavigationStart) {
                    this.handleNavigationStart(event, nonRedirectUrls);
                }

                if (event instanceof NavigationEnd) {
                    this.handleNavigationEnd(event, nonRedirectUrls);
                }
            });

        this.setupStyleSheet();
    }

    private removeIndexSpinner() {
        const indexSpinner = document.getElementById('indexSpinner');

        if (indexSpinner)
            indexSpinner.remove();
    }

    private setupStyleSheet() {
        const stylesheet = this.appService.getConfigurationRaw(STYLESHEET);

        if (stylesheet !== undefined && stylesheet !== null) {
            this.appService.loadStylesheet(`${stylesheet}?v=${this.version}`, this.renderer);
        }
    }

    private handleNavigationStart(event: NavigationStart, nonRedirectUrls: string[]) {
        const isNonRedirectUrl = nonRedirectUrls.some(url => event.url.includes(url));

        if (!isNonRedirectUrl) {
            const previousUrl = event.url;
            if (previousUrl) {
                // remove the client from the url to get the route
                const url = previousUrl.split('/').slice(2).join('/');
                this.prevRoute.url = url;

                // remove the query params from the url
                const index = this.prevRoute.url.indexOf('?');
                if (index > -1) {
                    this.prevRoute.url = this.prevRoute.url.substring(0, index);
                }
            }
            else {
                this.prevRoute.url = '/';
            }

            const prevRoute = JSON.parse(sessionStorage.getItem('prev-route') as string);
            //const prevUrl = sessionStorage.getItem('prev-url');
            const params = {
                previousUrl: location.pathname + location.search,
            }

            if (prevRoute?.params?.previousUrl !== params.previousUrl && previousUrl !== params.previousUrl) {
                this.prevRoute.params = params;

                sessionStorage.setItem('prev-route', JSON.stringify(this.prevRoute));
                sessionStorage.setItem('prev-url', location.pathname + location.search);
            }
        }

    }

    private handleNavigationEnd(event: NavigationEnd, nonRedirectUrls: string[]) {
        let route = this.activatedRoute.firstChild;
        while (route?.firstChild) {
            route = route.firstChild;
        }

        if (route?.snapshot) {
            const isNonRedirectUrl = nonRedirectUrls.some(url => event.url.includes(url));
            const { params, queryParams } = route.snapshot;
            const { state } = history;
            const stateCopy = { ...state };

            delete stateCopy.navigationId;

            if (!isNonRedirectUrl) {
                this.updatePrevRoute(params, queryParams, stateCopy);
                sessionStorage.setItem('prev-route', JSON.stringify(this.prevRoute));
            }
        }
    }

    private updatePrevRoute(params: any, queryParams: any, state: any) {
        if (params && Object.keys(params).length) {
            this.prevRoute.params = { ...this.prevRoute.params, ...params };
        }

        if (queryParams && Object.keys(queryParams).length) {
            this.prevRoute.queryParams = queryParams;
        }
        else {
            delete this.prevRoute.queryParams;
        }

        if (state && Object.keys(state).length) {
            this.prevRoute.state = state;
        }
        else {
            delete this.prevRoute.state;
        }
    }

    skipToContent(event?: KeyboardEvent) {
        // Only focus on content for Enter or Space key presses or if no event is provided (clicking the link)
        if (event?.code === 'Enter' || event?.code === 'Space' || event === undefined) {
            this.content.nativeElement.focus();
        }
    }
}
